/**
 * Play a subtle 2-note chime using the Web Audio API — no asset required.
 * Users can mute globally via the `stratos.chime.muted` localStorage flag.
 */
let ctx = null;

function getCtx() {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  } catch {
    ctx = null;
  }
  return ctx;
}

export function isChimeMuted() {
  try {
    return localStorage.getItem("stratos.chime.muted") === "1";
  } catch {
    return false;
  }
}

export function setChimeMuted(muted) {
  try {
    localStorage.setItem("stratos.chime.muted", muted ? "1" : "0");
  } catch {}
}

// Iter 53 — persisted volume multiplier (0.0 - 1.0). Stored as an integer
// 0-100 in localStorage for easy slider round-tripping. Default is 1.0 so
// existing users hear the chime at its historical amplitude.
export function getChimeVolume() {
  try {
    const raw = localStorage.getItem("stratos.chime.volume");
    if (raw == null) return 1.0;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return 1.0;
    return Math.max(0, Math.min(100, n)) / 100;
  } catch {
    return 1.0;
  }
}

export function setChimeVolume(pct) {
  try {
    const n = Math.max(0, Math.min(100, Math.round(pct)));
    localStorage.setItem("stratos.chime.volume", String(n));
  } catch {}
}

export function playChime() {
  if (isChimeMuted()) return;
  const c = getCtx();
  if (!c) return;
  // Safari + mobile Chrome sometimes leave the AudioContext suspended until a
  // user gesture. resume() is safe to call whenever — no-op if already running.
  if (c.state === "suspended") {
    try { c.resume(); } catch {}
  }

  // Iter 53 — scale the peak amplitude by the persisted volume multiplier.
  // 0.06 remains the historical peak (~-24dB); at volume=0 the chime is
  // silent (isChimeMuted() covers the true-mute path; this path lets users
  // pull it down for shared-workspace comfort without full mute).
  const vol = getChimeVolume();
  const PEAK = 0.06 * vol;

  // 2-note descending arpeggio (E5 → C5) — pleasant, ~180ms
  const NOTES = [
    { freq: 659.25, at: 0.0, dur: 0.16 },
    { freq: 523.25, at: 0.09, dur: 0.24 },
  ];

  emitNotes(c, NOTES, PEAK);
}

/**
 * Iter 60 — Ring Pulse Custom Note. A distinct 2-note ASCENDING pattern
 * (C5 → E5) that admins can identify by ear as "a since-visit delta
 * flipped sign" — vs the descending general chime used elsewhere.
 * Respects the same mute + volume settings as ``playChime``.
 */
export function playFlipChime() {
  if (isChimeMuted()) return;
  const c = getCtx();
  if (!c) return;
  if (c.state === "suspended") {
    try { c.resume(); } catch {}
  }
  const vol = getChimeVolume();
  const PEAK = 0.06 * vol;
  const NOTES = [
    { freq: 523.25, at: 0.0, dur: 0.16 },  // C5
    { freq: 659.25, at: 0.09, dur: 0.24 }, // E5
  ];
  emitNotes(c, NOTES, PEAK);
}

/**
 * Iter 62 — Chime Test Comparison. Fires playChime (descending) then
 * playFlipChime (ascending) ~600ms apart so admins can hear the two
 * chimes back-to-back in a single tap. Returns a promise that resolves
 * after both have been scheduled. Respects mute globally.
 */
export function playBothChimes() {
  if (isChimeMuted()) return;
  playChime();
  window.setTimeout(() => {
    if (!isChimeMuted()) playFlipChime();
  }, 600);
}

/**
 * Iter 64 — Reverse Chime Play. Fires playFlipChime (ascending) first
 * then playChime (descending) ~600ms later so admins can compare the
 * reversed order. Respects mute globally.
 */
export function playBothChimesReverse() {
  if (isChimeMuted()) return;
  playFlipChime();
  window.setTimeout(() => {
    if (!isChimeMuted()) playChime();
  }, 600);
}

/**
 * Iter 60 — extracted from playChime so playFlipChime can reuse the
 * same attack-decay envelope without duplicating the oscillator glue.
 */
function emitNotes(c, notes, peak) {
  notes.forEach(({ freq, at, dur }) => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    // Attack-decay envelope so it doesn't click
    const t0 = c.currentTime + at;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(peak, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  });
}
